import React, { useState, useEffect } from 'react';
import { useNavigate, Link, useSearchParams, useLocation } from 'react-router-dom';
import { parsePhoneNumberFromString } from 'libphonenumber-js';
import {
  Container,
  Paper,
  TextField,
  Button,
  Typography,
  Box,
  Alert,
  CircularProgress,
  InputAdornment,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Checkbox,
  FormControlLabel,
  IconButton,
  Popover,
  List,
  ListItemButton,
  ListItemText,
} from '@mui/material';
import {
  Email,
  Person,
  Business,
  Phone,
  ArrowBack as ArrowBackIcon,
  WhatsApp as WhatsAppIcon,
  Search as SearchIcon,
  ArrowDropDown as ArrowDropDownIcon,
} from '@mui/icons-material';
import { useMerchantAuth } from '../../../contexts/MerchantAuthContext';
import { masterDataConfigAPI } from '../../../services/api';
import { merchantAuthAPI } from '../../../services/merchantAPI';
import { isIndiaCountry } from '../../../config/businessConstants';
import FlutterServicesAPI from '../../../services/flutterServices';

// DB limits (users.name / users.company_name)
const MAX_FULL_NAME_LEN = 100;
const MAX_COMPANY_NAME_LEN = 100;
const MAX_LOCAL_PHONE_DIGITS = 15;
// Policy versions stored with consent audit records.
const TERMS_POLICY_VERSION = '2025-08-15';
const PRIVACY_POLICY_VERSION = '2025-08-15';

// Same pattern as backend merchant email validation
const isValidRegistrationEmail = (email) => {
  const s = String(email).trim();
  if (!s || s.length > 254) return false;
  return /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/.test(s);
};

// Strip spaces/dashes; local number only (country code is separate in UI)
const normalizeLocalPhoneDigits = (raw) => String(raw || '').replace(/[\s\u00A0]/g, '').replace(/-/g, '');

const isIndiaSelected = (countryId) =>
  isIndiaCountry(countryId == null || countryId === '' ? null : Number(countryId));

// Resolve default industry from /master-data/industry_types (Jewelry row in DB)
const pickJewelryIndustry = (list) => {
  if (!list || !list.length) return null;
  return (
    list.find(
      (t) =>
        (t.name && String(t.name).toLowerCase() === 'jewelry') ||
        (t.display_name && String(t.display_name).toLowerCase() === 'jewelry')
    ) || null
  );
};

const MerchantRegister = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    mobile: '',
    mobile_code: '+91',  // Default mobile code for India
    country_id: null,
    company_name: '',
    gst_number: '',
    address: '',
    industry_type_id: '',
    industry_type_name: '',
    referral_code: '',
    password: '',
    confirmPassword: '',
    state_id: '',
    city_id: '',
    pincode: ''
  });

  const [termsAccepted, setTermsAccepted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [fieldErrors, setFieldErrors] = useState({});
  const [countries, setCountries] = useState([]);
  const [showWhatsAppSmsAuth, setShowWhatsAppSmsAuth] = useState(true);

  const { register } = useMerchantAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const [countryAnchorEl, setCountryAnchorEl] = useState(null);
  const [countrySearch, setCountrySearch] = useState('');
  const searchInputRef = React.useRef(null);
  const selectedCountryRef = React.useRef(null);

  useEffect(() => {
    const checkAuthVisibility = async () => {
      try {
        const response = await merchantAuthAPI.checkAuthVisibility();
        if (response.data && typeof response.data.show_whatsapp_sms_auth === 'boolean') {
          setShowWhatsAppSmsAuth(response.data.show_whatsapp_sms_auth);
        }
      } catch (error) {
        console.error('❌ MerchantRegister: Failed to check auth visibility:', error);
      }
    };
    checkAuthVisibility();
  }, []);

  useEffect(() => {
    const referralCode = searchParams.get('ref') || searchParams.get('referral_code');
    const updates = {};
    
    if (referralCode) {
      updates.referral_code = referralCode;
    }
    
    if (location.state?.prefillContact) {
      if (location.state.contactType === 'email') {
        updates.email = location.state.prefillContact;
      } else if (location.state.contactType === 'phone') {
        updates.mobile = location.state.prefillContact;
      }
    }

    // Handle country_code from location state (passed from login when user not found)
    if (location.state?.country_code) {
      updates.mobile_code = location.state.country_code;
      // Use country_id from location state if available (preserves exact country when multiple countries share same code)
      if (location.state?.country_id) {
        updates.country_id = location.state.country_id;
      } else {
        // Fallback: find and set country_id based on country_code
        const country = countries.find(c => c.country_code === location.state.country_code);
        if (country) {
          updates.country_id = country.id;
        }
      }
    }

    if (Object.keys(updates).length > 0) {
      setFormData(prev => ({ ...prev, ...updates }));
    }
  }, [searchParams, location.state, countries]);

  const handleCountryClick = (event) => {
    setCountryAnchorEl(event.currentTarget);
    setCountrySearch('');
  };

  const handleCountryClose = () => {
    setCountryAnchorEl(null);
    setCountrySearch('');
  };

  const handleCountrySelect = (country) => {
    setFormData(prev => ({
      ...prev,
      mobile_code: country.country_code,
      country_id: country.id
    }));
    handleCountryClose();
  };

  const filteredCountries = countries.filter(c =>
    (c.country_name || '').toLowerCase().includes(countrySearch.toLowerCase()) ||
    (c.country_code || '').includes(countrySearch)
  );

  useEffect(() => {
    if (countryAnchorEl) {
      setTimeout(() => {
        if (searchInputRef.current) {
          searchInputRef.current.focus();
        }
        if (selectedCountryRef.current) {
          selectedCountryRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
      }, 100);
    }
  }, [countryAnchorEl]);

  useEffect(() => {
    const loadInitialData = async () => {
      try {
        setLoading(true);

        const countriesResponse = await masterDataConfigAPI.getCountries();
        setCountries(countriesResponse.data.data || []);

        let industryUpdate = {};
        try {
          const industryResponse = await masterDataConfigAPI.getIndustryTypes();
          const industryList = industryResponse.data?.data || [];
          const jewelry = pickJewelryIndustry(industryList);
          if (jewelry) {
            industryUpdate = {
              industry_type_id: jewelry.id,
              industry_type_name: jewelry.name
            };
          }
        } catch (indErr) {
          // Registration API still defaults jewelry server-side if this fails
          console.error('Failed to load industry types:', indErr);
        }

        // Detect default country from IP instead of always defaulting to India
        // Only use IP detection if not already set by location.state (from login redirect)
        let detectedCountryId = null;
        let detectedMobileCode = '+91';

        // Check if country_code is already set from location.state (login redirect)
        if (!location.state?.country_code) {
          try {
            const detectResponse = await merchantAuthAPI.detectCountry();
            if (detectResponse.data && detectResponse.data.country_id) {
              detectedCountryId = detectResponse.data.country_id;
              detectedMobileCode = detectResponse.data.country_code || '+91';
            }
          } catch (detectErr) {
            console.error('Failed to detect country from IP:', detectErr);
          }

          // Fallback to India if detection failed
          if (!detectedCountryId && countriesResponse.data.data && countriesResponse.data.data.length > 0) {
            const indiaCountry = countriesResponse.data.data.find(c => isIndiaCountry(c.id));
            if (indiaCountry) {
              detectedCountryId = Number(indiaCountry.id);
              detectedMobileCode = indiaCountry.country_code || '+91';
            }
          }

          setFormData(prev => ({
            ...prev,
            country_id: detectedCountryId,
            mobile_code: detectedMobileCode,
            ...industryUpdate
          }));
        } else {
          // If location.state has country_code, only set industry update
          setFormData(prev => ({
            ...prev,
            ...industryUpdate
          }));
        }
      } catch (error) {
        console.error('Failed to load initial data:', error);
      } finally {
        setLoading(false);
      }
    };

    loadInitialData();
  }, []);

  useEffect(() => {
    const isMobile = window.innerWidth < 600; // xs breakpoint

    if (isMobile) {
      document.body.style.backgroundColor = 'white';
    } else {
      document.body.style.backgroundColor = '#f5f5f5';
    }

    return () => {
      document.body.style.backgroundColor = '#f5f5f5';
    };
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    
    // Alphanumeric check for Full Name - Only allow alphabets and spaces
    if (name === 'name') {
      if (value !== '' && !/^[A-Za-z\s]+$/.test(value)) {
        return; // Reject the change
      }
    }

    setFormData({
      ...formData,
      [name]: value
    });
    setFieldErrors((prev) => ({ ...prev, [name]: '' }));
    setError('');
  };

  const handleCountryChange = (e) => {
    const selectedCountryId = e.target.value;
    // MUI Select may stringify ids — compare numerically for TC07 (non-India codes)
    const selectedCountry = countries.find(
      (country) => Number(country.id) === Number(selectedCountryId)
    );

    setFormData({
      ...formData,
      country_id: selectedCountryId === '' ? null : Number(selectedCountryId),
      mobile_code: selectedCountry ? selectedCountry.country_code : '',
    });

    setFieldErrors((prev) => ({ ...prev, country_id: '' }));
    setError('');
  };

  const validateForm = async () => {
    const errors = {};

    const nameTrim = formData.name.trim();
    const companyTrim = formData.company_name.trim();
    const emailTrim = formData.email.trim();
    const mobileDigits = normalizeLocalPhoneDigits(formData.mobile);

    // TC02 — required fields (after trim)
    if (!nameTrim) {
      errors.name = 'Full name is required';
    } else if (!/^[A-Za-z\s]+$/.test(nameTrim)) {
      errors.name = 'Full name can only contain alphabets and spaces';
    }
    if (!companyTrim) errors.company_name = 'Company name is required';
    if (!emailTrim) errors.email = 'Email address is required';
    else if (!isValidRegistrationEmail(formData.email)) {
      // TC03 — invalid email (e.g. jane.smith.com, jane@smith)
      errors.email = 'Please enter a valid email address';
    }

    // TC05 — length limits (also enforced via maxLength on inputs)
    if (nameTrim && nameTrim.length > MAX_FULL_NAME_LEN) {
      errors.name = `Full name cannot exceed ${MAX_FULL_NAME_LEN} characters`;
    }
    if (companyTrim && companyTrim.length > MAX_COMPANY_NAME_LEN) {
      errors.company_name = `Company name cannot exceed ${MAX_COMPANY_NAME_LEN} characters`;
    }

    // TC06 — block angle brackets / newlines only; other special characters allowed
    if (nameTrim && /[\r\n<>]/.test(nameTrim)) {
      errors.name = 'Full name contains invalid characters';
    }
    if (companyTrim && /[\r\n<>]/.test(companyTrim)) {
      errors.company_name = 'Company name contains invalid characters';
    }

    // TC04 — use libphonenumber-js for robust phone validation
    // During app review, mobile number is optional — only validate if filled in
    const mobileRequired = showWhatsAppSmsAuth;
    if (mobileRequired && (!formData.mobile || !String(formData.mobile).trim())) {
      errors.mobile = 'Please enter a valid whatsapp number';
    } else if (mobileDigits) {
      // Validate format using libphonenumber-js
      try {
        const mobileValue = formData.mobile.trim();
        const countryCode = formData.mobile_code || '+91';
        const countryCodeWithoutPlus = countryCode.replace('+', '');

        console.log('🔍 Registration Phone Validation:');
        console.log('  - mobileValue:', mobileValue);
        console.log('  - countryCode:', countryCode);
        console.log('  - mobileDigits:', mobileDigits);

        let phoneNumber = null;

        // Try parsing as international if it starts with +
        if (mobileValue.startsWith('+')) {
          phoneNumber = parsePhoneNumberFromString(mobileValue);
          console.log('  - Attempt 1 (with +):', phoneNumber);
        }

        // If no result, prepend country code and try as international
        // Skip embedded code check for single-digit country codes (like +7) to avoid ambiguity
        if (!phoneNumber) {
          const withCountryCode = countryCode + mobileValue;
          phoneNumber = parsePhoneNumberFromString(withCountryCode);
          console.log('  - Attempt 2 (with country code):', withCountryCode, '→', phoneNumber);
        }

        console.log('  - Final phoneNumber:', phoneNumber);
        console.log('  - isValid:', phoneNumber?.isValid());

        if (!phoneNumber || !phoneNumber.isValid()) {
          errors.mobile = 'Please enter a valid whatsapp number';
        }
      } catch (error) {
        console.error('❌ Phone validation error:', error);
        errors.mobile = 'Please enter a valid whatsapp number';
      }
    }

    if (!termsAccepted) {
      errors.terms = 'You must accept the terms and conditions';
    }

    // Remote checks: MailboxValidator + blocklist + duplicates (aligned with /auth/register)
    if (Object.keys(errors).length === 0) {
      try {
        const emailLower = emailTrim.toLowerCase();
        const digitsForApi = normalizeLocalPhoneDigits(formData.mobile);
        const emailResp = await merchantAuthAPI.checkEmailAvailability(emailLower);
        if (!emailResp.data?.available) {
          errors.email = emailResp.data?.message || 'Please enter a valid email address';
        } else if (digitsForApi) {
          // Only check mobile availability if a number was actually entered
          const mobileResp = await merchantAuthAPI.checkMobileAvailability(
            digitsForApi,
            formData.mobile_code
          );
          if (!mobileResp.data?.available) {
            errors.mobile = mobileResp.data?.message || 'This number is not available';
          }
        }
      } catch (err) {
        console.error(err);
        setError('Could not verify email or phone. Please try again.');
        setFieldErrors(errors);
        return false;
      }
    }

    setFieldErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    setLoading(true);
    setError('');

    const isValid = await validateForm();
    if (!isValid) {
      setLoading(false);
      return;
    }

    try {
      const nameTrim = formData.name.trim();
      const companyTrim = formData.company_name.trim();
      const mobileDigits = normalizeLocalPhoneDigits(formData.mobile);

      const registrationData = {
        name: nameTrim,
        email: formData.email.trim().toLowerCase(),
        mobile: mobileDigits,
        mobile_code: formData.mobile_code,
        // Same WhatsApp digits as mobile so DB has both columns for OTP and dedupe
        whatsapp_number: mobileDigits,
        whatsapp_code: formData.mobile_code,
        company_name: companyTrim,
        country_id: formData.country_id == null || formData.country_id === '' ? null : Number(formData.country_id),
        gst_number: '',
        address: '',
        // Default Jewelry from master data when loaded; backend also defaults if omitted
        industry_type_id:
          formData.industry_type_id !== '' && formData.industry_type_id != null
            ? Number(formData.industry_type_id)
            : null,
        industry_type: formData.industry_type_name || null,
        referral_code: formData.referral_code.trim() || null,
        password: '', // Kept empty or dummy as per requirement
        state_id: '',
        city_id: '',
        pincode: '',
        // Single checkbox UI drives both legal consents as requested.
        terms_accepted: termsAccepted,
        privacy_accepted: termsAccepted,
        terms_version: TERMS_POLICY_VERSION,
        privacy_version: PRIVACY_POLICY_VERSION
      };

      const result = await register(registrationData);

      if (result.success) {
        // If no mobile provided (app review mode), use email-only OTP flow
        const hasPhone = !!mobileDigits;
        navigate('/verify-otp', {
          state: {
            contact: hasPhone ? mobileDigits : formData.email.trim().toLowerCase(),
            email: formData.email,
            contactType: hasPhone ? 'dual' : 'email',
            country_code: formData.mobile_code,
            fromRegistration: true
          }
        });
      } else {
        // Handle field-specific errors if returned by backend
        if (result.field) {
          setFieldErrors(prev => ({ ...prev, [result.field]: result.error }));
        } else {
          setError(result.error);
        }
      }
    } catch (error) {
      setError('An unexpected error occurred. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Container
      component="main"
      maxWidth={false}
      data-testid="merchant-register-page"
      sx={{ maxWidth: '560px', mx: 'auto', minHeight: '100vh', display: 'flex', flexDirection: 'column' }}
    >
      <Box
        sx={{
          marginTop: { xs: 0.5, sm: 8 },
          marginBottom: { xs: 1, sm: 0 },
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          flexGrow: 1,
        }}
      >
        <Paper
          elevation={0}
          sx={{
            pt: { xs: 2, sm: 4, md: 6 },
            pb: { xs: 3, sm: 5, md: 7 },
            pl: { xs: 2, sm: 4, md: 6 },
            pr: { xs: 2, sm: 3, md: 5 },
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            borderRadius: '18px',
            width: '100%',
            boxShadow: { xs: 'none', sm: 3 },
            border: { xs: 'none', sm: '1px solid #E0E0E0' },
            minHeight: '80vh',
            '@media (min-width: 414px)': {
              pt: 2,
              pb: 2,
              pl: 4,
              pr: 4,
            }
          }}
        >
          {error && (
            <Alert severity="error" sx={{ width: '100%', mb: 2 }}>
              {error}
            </Alert>
          )}

          <Box sx={{ width: '100%', display: 'flex', flexDirection: 'column', flex: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 4, mt: 2, position: 'relative' }}>
              <IconButton onClick={() => navigate('/login')} sx={{ position: 'absolute', left: 0, color: '#1A1A1A' }}>
                <ArrowBackIcon />
              </IconButton>
              <Typography variant="h6" className="font-montserret-semibold" sx={{ fontWeight: 600, fontSize: '18px', color: '#1A1A1A', width: '100%', textAlign: 'center' }}>
                Add Your Details
              </Typography>
            </Box>

            <Box component="form" onSubmit={handleSubmit} sx={{ width: '100%' }}>
              <Grid container spacing={1}>
                <Grid item xs={12}>
                  <Box sx={{ display: 'flex', mb: 1 }}>
                    <Typography variant="body2" className="form-label">
                      Full Name <span style={{ color: '#A864ED' }}>*</span>
                    </Typography>
                  </Box>
                  <TextField
                    fullWidth
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    disabled={loading}
                    placeholder="Full Name"
                    error={!!fieldErrors.name}
                    helperText={fieldErrors.name}
                    className="form-control muiBase-control"
                    inputProps={{ maxLength: MAX_FULL_NAME_LEN }}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <Box component="img" src="/assets/img/icons/profile_icon.svg" alt="profile" sx={{ width: 20, height: 20 }} />
                        </InputAdornment>
                      )
                    }}
                  />
                </Grid>

                <Grid item xs={12}>
                  <Box sx={{ display: 'flex', mb: 1 }}>
                    <Typography variant="body2" className="form-label">
                      Company Name <span style={{ color: '#A864ED' }}>*</span>
                    </Typography>
                  </Box>
                  <TextField
                    fullWidth
                    name="company_name"
                    value={formData.company_name}
                    onChange={handleChange}
                    disabled={loading}
                    placeholder="Company Name"
                    error={!!fieldErrors.company_name}
                    helperText={fieldErrors.company_name}
                    className="form-control muiBase-control"
                    inputProps={{ maxLength: MAX_COMPANY_NAME_LEN }}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <Box component="img" src="/assets/img/icons/company_icon.svg" alt="company" sx={{ width: 20, height: 20 }} />
                        </InputAdornment>
                      )
                    }}
                  />
                </Grid>

                <Grid item xs={12}>
                  <Box sx={{ display: 'flex', mb: 1 }}>
                    <Typography variant="body2" className="form-label">
                      Email Address <span style={{ color: '#A864ED' }}>*</span>
                    </Typography>
                  </Box>
                  <TextField
                    fullWidth
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    disabled={loading}
                    placeholder="Email Address"
                    error={!!fieldErrors.email}
                    helperText={fieldErrors.email}
                    className="form-control muiBase-control"
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <Box component="img" src="/assets/img/icons/email_icon.svg" alt="email" sx={{ width: 20, height: 20 }} />
                        </InputAdornment>
                      )
                    }}
                  />
                </Grid>

                <Grid item xs={12}>
                  <Box sx={{ display: 'flex', mb: 1 }}>
                    <Typography variant="body2" className="form-label">
                      {showWhatsAppSmsAuth ? (
                        <>Whatsapp Number <span style={{ color: '#A864ED' }}>*</span></>
                      ) : (
                        'Mobile Number'
                      )}
                    </Typography>
                  </Box>
                  <Grid container spacing={1}>
                    <Grid item xs={4} sm={4} mb={{ xs: 0, md: 0 }}>
                      <Box
                        onClick={handleCountryClick}
                        sx={{
                          height: 'var(--input-height-44)',
                          display: 'flex',
                          alignItems: 'center',
                          px: '12px',
                          border: '1px solid #E0E0E0',
                          borderRadius: '10px',
                          backgroundColor: '#FFFFFF',
                          cursor: 'pointer',
                          '&:hover': {
                            borderColor: '#BDBDBD',
                          },
                          fontFamily: 'var(--font-montserrat-medium)',
                          fontSize: 'var(--font-size-small)',
                          color: '#1A1A1A',
                          justifyContent: 'space-between'
                        }}
                      >
                        <span>{formData.mobile_code}</span>
                        <ArrowDropDownIcon sx={{ fontSize: 20, color: '#666' }} />
                      </Box>

                      {/* Country Code Popover */}
                      <Popover
                        open={Boolean(countryAnchorEl)}
                        anchorEl={countryAnchorEl}
                        onClose={handleCountryClose}
                        anchorOrigin={{
                          vertical: 'bottom',
                          horizontal: 'left',
                        }}
                        transformOrigin={{
                          vertical: 'top',
                          horizontal: 'left',
                        }}
                        PaperProps={{
                          sx: {
                            width: '300px',
                            maxHeight: '350px',
                            borderRadius: '12px',
                            boxShadow: '0 8px 32px rgba(0,0,0,0.12)',
                            mt: 0.5,
                          }
                        }}
                      >
                        {/* Search field */}
                        <Box sx={{ p: 1.5, borderBottom: '1px solid #f0f0f0' }}>
                          <TextField
                            inputRef={searchInputRef}
                            fullWidth
                            size="small"
                            placeholder="Search country..."
                            value={countrySearch}
                            onChange={(e) => setCountrySearch(e.target.value)}
                            InputProps={{
                              startAdornment: (
                                <InputAdornment position="start">
                                  <SearchIcon sx={{ fontSize: 18, color: '#9e9e9e' }} />
                                </InputAdornment>
                              ),
                            }}
                            sx={{
                              '& .MuiOutlinedInput-root': {
                                borderRadius: '8px',
                                fontSize: '13px',
                                fontFamily: 'var(--font-montserrat-medium)',
                              }
                            }}
                          />
                        </Box>
                        {/* Country list */}
                        <List sx={{ p: 0, overflow: 'auto', maxHeight: '280px' }}>
                          {filteredCountries.map((country) => (
                            <ListItemButton
                              key={country.id}
                              ref={String(country.id) === String(formData.country_id) ? selectedCountryRef : null}
                              onClick={() => handleCountrySelect(country)}
                              selected={String(country.id) === String(formData.country_id)}
                              sx={{
                                py: 0.8,
                                px: 2,
                                '&.Mui-selected': {
                                  backgroundColor: '#f3e8ff',
                                },
                                '&:hover': {
                                  backgroundColor: '#f9f5ff',
                                },
                              }}
                            >
                              <ListItemText
                                primary={
                                  <Typography sx={{ fontSize: '13px', fontFamily: 'var(--font-montserrat-medium)', color: '#1A1A1A' }}>
                                    <span style={{ display: 'inline-block', width: '45px', textAlign: 'left' }}>{country.country_code}</span>
                                    {country.country_name}
                                  </Typography>
                                }
                              />
                            </ListItemButton>
                          ))}
                          {filteredCountries.length === 0 && (
                            <List sx={{ py: 2 }}>
                              <ListItemText
                                primary={
                                  <Typography sx={{ fontSize: '13px', color: '#9e9e9e', textAlign: 'center' }}>
                                    No countries found
                                  </Typography>
                                }
                              />
                            </List>
                          )}
                        </List>
                      </Popover>
                      {fieldErrors.country_id && (
                        <Typography variant="caption" color="error">{fieldErrors.country_id}</Typography>
                      )}
                    </Grid>
                    <Grid item xs={8} sm={8} mb={{ xs: 0, md: 0 }}>
                      <TextField
                        fullWidth
                        name="mobile"
                        value={formData.mobile}
                        onChange={handleChange}
                        disabled={loading}
                        placeholder={showWhatsAppSmsAuth ? 'Whatsapp Number' : 'Mobile Number (optional)'}
                        error={!!fieldErrors.mobile}
                        helperText={fieldErrors.mobile}
                        className="form-control muiBase-control"
                        inputProps={{ maxLength: MAX_LOCAL_PHONE_DIGITS + 4, inputMode: 'numeric' }}
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              {showWhatsAppSmsAuth ? (
                                <Box component="img" src="/assets/img/icons/whatsapp_icon.svg" alt="whatsapp" sx={{ width: 20, height: 20 }} />
                              ) : (
                                <Phone sx={{ color: '#9e9e9e', fontSize: 20 }} />
                              )}
                            </InputAdornment>
                          )
                        }}
                      />
                    </Grid>
                  </Grid>
                </Grid>

                <Grid item xs={12} sx={{ display: 'flex', justifyContent: 'center' }} mt={{ xs: 0, md: 0 }}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={termsAccepted}
                        onChange={(e) => setTermsAccepted(e.target.checked)}
                        sx={{ color: '#6318C6', '&.Mui-checked': { color: '#A864ED' } }}
                      />
                    }
                    label={
                      <Typography variant="body2" className="login-text">
                        I Accept the <Link to="/terms-conditions" style={{ color: '#A864ED', textDecoration: 'underline' }}>Terms and Conditions</Link>
                      </Typography>
                    }
                  />
                </Grid>
                {fieldErrors.terms && (
                  <Grid item xs={12} sx={{ display: 'flex', justifyContent: 'center' }} mt={{ xs: 0, md: 0 }}>
                    <Typography variant="caption" color="error" sx={{ display: 'block' }}>
                      {fieldErrors.terms}
                    </Typography>
                  </Grid>
                )}
              </Grid>

              <Button
                type="submit"
                fullWidth
                variant="contained"
                sx={{
                  mt: 3,
                  mb: 2,
                  background: 'linear-gradient(90deg, #A864ED 0%, #F4B876 100%)',
                  borderRadius: '10px',
                  textTransform: 'none',
                  fontSize: '16px',
                  fontWeight: 600,
                  py: 1.5,
                  boxShadow: 'none',
                  '&:hover': {
                    background: 'linear-gradient(90deg, #9b5ce0 0%, #e8ad6b 100%)',
                    boxShadow: 'none',
                  }
                }}
                disabled={loading}
              >
                {loading ? (
                  <CircularProgress size={24} color="inherit" />
                ) : (
                  'Create Account'
                )}
              </Button>
            </Box>
          </Box>
        </Paper>
      </Box>

    </Container>
  );
};

export default MerchantRegister;